import cn from 'classnames';
import { useStoreAvailability } from '@utils/store/useStoreAvailabilityNew';
import { useUI } from '@context/GlobalUIContext';
import { usePdp } from '@context/PdpContext';
import { useStoresUI } from '@context/StoresContext';
import { parseAvailabilityMessage } from '@utils/parseAvailabilityMessage';
import { useLocale } from '@hooks/useLocale';
import { useSpuItemAvailabilityVariant } from '@hooks/useSpuItemAvailabilityVariant';
import Skeleton from 'react-loading-skeleton';
import Button from '@components/ui/Button';
import IconWrapper from '@components/ui/Icons';
import { FEATURE_FLAG_DISABLE_SPU } from '@config/feature-flag';

import styles from './StoreAvailability.module.scss';

const StoreAvailability = ({
  storeId,
  pickupMessage,
}: {
  storeId: string;
  pickupMessage?: string;
}) => {
  const { openStockLevelModal } = useUI();
  const { selectedVariant, shippingRestrictions, quantity } = usePdp();
  const { favouriteStoreDetails, setLocationAndFavouriteStore } = useStoresUI();
  const locale = useLocale();
  const result = useStoreAvailability(
    storeId,
    pickupMessage ?? null,
    locale ?? 'en',
    quantity ?? null
  );

  const { data } = useSpuItemAvailabilityVariant({
    selectedVariant: selectedVariant,
    productName: 'Product Name',
    shippingRestrictions: shippingRestrictions,
    quantity: quantity ?? null,
  });

  const availabilityMessageForStore = data?.find(
    (item) => item.store.data.mec_store_id === storeId
  );

  // Parse the availability message for clickable store name
  const parsedMessage = availabilityMessageForStore?.message?.short
    ? parseAvailabilityMessage(
        availabilityMessageForStore.message.short,
        locale
      )
    : null;

  if (!result) return null;

  const {
    stockMessage,
    availabilityMessage,
    stockLevelMessage,
    stockLevel,
    availableInOtherStores,
  } = result;

  if (!availabilityMessage) return null;

  const chooseStore = () => {
    setLocationAndFavouriteStore(favouriteStoreDetails.data.geo);
    openStockLevelModal();
  };

  return (
    <div className={styles.wrapper}>
      <span className={styles.availabilityText}>
        {FEATURE_FLAG_DISABLE_SPU ? (
          stockMessage
        ) : (
          <div className={styles.availabilityMessageContainer}>
            <div>
              {parsedMessage ? (
                <>
                  <span>{parsedMessage.beforeStore}</span>
                  {parsedMessage.storeName && (
                    <>
                      {' '}
                      <Button
                        variant="unstyled"
                        size="small"
                        className={styles.storeNameButton}
                        label={parsedMessage.storeName}
                        handleClick={chooseStore}
                      />
                    </>
                  )}
                  {parsedMessage.afterStore && (
                    <>
                      {/* No space before punctuation, as in "Halifax." */}
                      {!/^[.,]/.test(parsedMessage.afterStore) && ' '}
                      <span>{parsedMessage.afterStore}</span>
                    </>
                  )}
                  {(parsedMessage.secondLine ||
                    parsedMessage.secondLineLink) && (
                    <span className={styles.secondLine}>
                      {parsedMessage.secondLineLink && (
                        <>
                          <Button
                            variant="unstyled"
                            size="small"
                            className={styles.storeNameButton}
                            label={parsedMessage.secondLineLink}
                            handleClick={chooseStore}
                          />
                          {!/^[.,]/.test(parsedMessage.secondLine ?? '') && ' '}
                        </>
                      )}
                      {parsedMessage.secondLine}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <Skeleton height={14} width={200} />
                  <Skeleton height={14} width={200} />
                </>
              )}
            </div>
          </div>
        )}
        {stockLevelMessage && (
          <span className={styles.stockLevelContainer}>
            {stockLevel === 'low' && (
              <IconWrapper
                icon="fire"
                height={20}
                fill="var(--color-red)"
                width={20}
              />
            )}
            <p
              className={cn(
                styles.stockLevelMessage,
                stockLevel ? styles[stockLevel.toString()] : undefined
              )}
            >
              {stockLevelMessage}
            </p>
          </span>
        )}
        {availableInOtherStores &&
          stockLevel === 'outOfStock' &&
          !selectedVariant?.isMarketplace && (
            <span className={styles.availableInOtherStores}>
              {availableInOtherStores.start}
              <button
                className={styles.availableInOtherStoresButton}
                onClick={() => {
                  setLocationAndFavouriteStore(favouriteStoreDetails.data.geo);
                  openStockLevelModal();
                }}
              >
                {availableInOtherStores.end}
              </button>
            </span>
          )}
      </span>
    </div>
  );
};

export default StoreAvailability;
